import React, { useEffect } from 'react';
import { useState, useMemo } from 'react';
import { Country, State, City, ICountry, IState, ICity } from 'country-state-city';
import { Autocomplete, Box, Button, FormHelperText, TextField } from '@mui/material';
import { Address, LocationObject } from '../../../types/types';

export const LocationSelector = ({
	onSelect,
	helperText,
	returnObject,
	errors,
	address,
	skipCheck,
	widthProp,
	isRow,
	disableState,
	disableCity,
	educationalProfileEdit
}: {
	onSelect: (location: string | LocationObject) => void;
	helperText?: string;
	returnObject?: boolean;
	errors?: boolean;
	address?: Address | undefined;
	skipCheck?: boolean;
	widthProp?: string;
	isRow?: boolean;
	disableState?: boolean;
	disableCity?: boolean;
	educationalProfileEdit?: boolean;
}) => {
	const [country, setCountry] = useState<ICountry | null>();
	const [state, setState] = useState<IState | null>();
	const [city, setCity] = useState<ICity | null>();

	const countries = useMemo(() => {
		return Country.getAllCountries();
	}, [Country]);

	const states = useMemo(() => {
		if (!country) return [];
		return State.getStatesOfCountry(country.isoCode);
	}, [State, country]);

	const cities = useMemo(() => {
		if (!state || !country) return [];
		return City.getCitiesOfState(country.isoCode, state.isoCode);
	}, [City, state, country]);

	useEffect(() => {
		if (address) {
			if (skipCheck || country === undefined || country === null) {
				const tempCountry = countries.find((country) => country.name === address.country);
				setCountry(tempCountry);
				setState(null);
				setCity(null);
			}
			if (skipCheck || state === undefined || state === null) {
				const tempState = states.find((state) => state.name === address.state);
				setState(tempState);
				setCity(null);
			}
			if (skipCheck || city === undefined || city === null) {
				const tempCity = cities.find((city) => city.name === address.city);
				setCity(tempCity);
			}
		}
	});

	const submit = () => {
		const name = [city, state, country]
			.filter(Boolean)
			.map((c) => c?.name)
			.join(', ');

		onSelect(name);
	};

	const setLocation = (co?: ICountry | null, st?: IState | null, ci?: ICity | null) => {
		let locationObject: LocationObject = {} as LocationObject;
		if (educationalProfileEdit) {
			locationObject.city = ci;
			locationObject.state = st;
			locationObject.country = co;
		} else {
			locationObject = {
				city: ci ? ci : city,
				state: st ? st : state,
				country: co ? co : country
			};
		}
		if (returnObject) {
			onSelect(locationObject);
		}
	};

	return (
		<Box display={'flex'} flexDirection={isRow ? 'row' : 'column'} gap={2} width={widthProp}>
			<Autocomplete
				id="autocomplete"
				disablePortal
				options={countries}
				fullWidth
				getOptionLabel={(option) => option.name}
				isOptionEqualToValue={(option, value) => {
					return option.isoCode === value.isoCode;
				}}
				renderOption={(props, option) => (
					<li {...props} key={option.isoCode}>
						{option.flag} {option.name}
					</li>
				)}
				renderInput={(params) => (
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					<TextField
						{...params}
						InputLabelProps={{ style: { color: errors ? '' : country?.name ? 'black' : 'grey' } }}
						label={country?.name ? `${country.flag} ${country.name}` : 'Country of Residence'}
						variant="outlined"
						size="small"
						InputProps={{ ...params.InputProps, autoComplete: 'new-password' }}
					/>
				)}
				onChange={(_, c) => {
					setCountry(c);
					setState(null);
					setCity(null);
					if (returnObject) {
						setLocation(c, undefined, null);
					}
				}}
			/>
			{states.length && !disableState ? (
				<Autocomplete
					id="autocomplete2"
					disablePortal
					options={states}
					value={state}
					fullWidth
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => {
						return option.isoCode === value?.isoCode;
					}}
					renderInput={(params) => (
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						<TextField
							{...params}
							InputLabelProps={{ style: { color: state?.name ? 'black' : 'grey' } }}
							label={state?.name ? state.name : 'State'}
							variant="outlined"
							size="small"
							InputProps={{ ...params.InputProps, autoComplete: 'new-password' }}
						/>
					)}
					onChange={(_, c) => {
						setState(c);
						setCity(null);
						if (returnObject) {
							setLocation(country, c, null);
						}
					}}
				/>
			) : undefined}
			{cities.length && !disableCity ? (
				<Autocomplete
					id="autocomplete3"
					disablePortal
					options={cities}
					value={city}
					fullWidth
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => {
						return option.name === value?.name;
					}}
					renderInput={(params) => (
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						<TextField
							{...params}
							InputLabelProps={{ style: { color: city?.name ? 'black' : 'grey' } }}
							label={city?.name ? city.name : 'City'}
							variant="outlined"
							size="small"
							autoComplete="new-password"
							InputProps={{ ...params.InputProps, autoComplete: 'new-password' }}
						/>
					)}
					onChange={(_, c) => {
						setCity(c);
						if (returnObject) {
							setLocation(country, state, c);
						}
					}}
				/>
			) : undefined}
			{helperText && <FormHelperText sx={{ ml: 2, mt: -1 }}>{helperText}</FormHelperText>}

			{!returnObject && (
				<Button sx={{ textTransform: 'none' }} onClick={submit} variant="outlined">
					Select
				</Button>
			)}
		</Box>
	);
};
